import { action, computed, observable } from "mobx";
import Uppy from "@uppy/core";
import AwsS3 from "@uppy/aws-s3";
import uuidv4 from "uuid/v4";
import Immersive3dTourPanorama from "~/applications/immersive_3d_tour/models/Immersive3dTourPanorama";
import Immersive3dTourFloor from "~/applications/immersive_3d_tour/models/Immersive3dTourFloor";
import AwsS3UploadSignedUrlStore from "~/stores/AwsS3/AwsS3UploadSignedUrlStore";
import { validateAndExtractMetadata } from "~/applications/immersive_3d_tour/utils/panoramaValidation";

class Immersive3dTourTabStore {
  rootStore = null;

  /**
   * @property {Immersive3dTourStore|null}
   */
  immersive3dTourStore = null;

  /**
   * @property {AwsS3UploadSignedUrlStore|null}
   */
  awsS3UploadSignedUrlStore = null;

  /**
   * Pending client-side metadata extraction (Promise resolving to metadata or null), keyed by Uppy file ID.
   * @property {Object}
   */
  fileMetadataCache = {};

  /**
   * @property {int} propertyFloorsCount Transient value. Set by the user when choosing how many property floors
   */
  @observable propertyFloorsCount = 1;

  /**
   * @property {int} totalFloorsCreated Only used for tracking how many floors have been created in total so far
   */
  @observable totalFloorsCreated = 0;

  /**
   * @property {Immersive3dTourFloor[]} floors
   */
  @observable floors = [];

  /**
   * @property {Object}
   */
  @observable thumbnails = {};

  @observable uploader;

  @observable isUploading = false;

  @observable isTogglingToTour = false;

  @observable forceShowUpload = false;

  @observable hasUppyUploadError = false;

  /**
   * We'll track when a panorama is being dragged
   */
  @observable draggingPanoramaId = null;

  /**
   * We'll use this prop in order to figure out the panos multi select when holding down the SHIFT key.
   */
  @observable lastSelectedPanoramaId = null;

  /**
   * @property {Immersive3dTourFloor} bucketFloor;
   */
  @observable bucketFloor = null;

  @observable message = null;

  /**
   * @property {Object}
   */
  @observable errorMessages = {};

  constructor(immersive3dTourStore) {
    this.immersive3dTourStore = immersive3dTourStore;
    this.awsS3UploadSignedUrlStore = new AwsS3UploadSignedUrlStore();
    this.initializeBucketFloor();
  }

  setRootStore(rootStore) {
    this.rootStore = rootStore;
  }

  @computed
  get hasErrors() {
    if (this.bucketFloor.error) {
      return true;
    }

    for (let k = 0; k < this.bucketFloor.panoramas.length; k += 1) {
      if (this.bucketFloor.panoramas[k].hasErrors) {
        return true;
      }
    }

    for (let i = 0; i < this.floors.length; i += 1) {
      if (this.floors[i].error) {
        return true;
      }

      for (let j = 0; j < this.floors[i].panoramas.length; j += 1) {
        if (this.floors[i].panoramas[j].hasErrors) {
          return true;
        }
      }
    }

    return false;
  }

  @computed
  get panoramaErrorMessages() {
    const messages = [];
    const allFloors = [this.bucketFloor, ...this.floors];

    allFloors.forEach(floor => {
      floor.panoramas.forEach(panorama => {
        panorama.errors.forEach(error => messages.push(error.message));
      });
    });

    return [...new Set(messages)];
  }

  @computed
  get hasWarnings() {
    if (this.bucketFloor.hasWarnings) {
      return true;
    }

    for (let i = 0; i < this.floors.length; i += 1) {
      for (let j = 0; j < this.floors[i].panoramas.length; j += 1) {
        if (this.floors[i].panoramas[j].hasWarnings) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Tell how many panoramas are still left in the bucket that should belong to a floor in the context of
   * a Residential immersive 3d tour. When the immersive 3d tour is Commercial, then all panos should stay
   * at the bucket, in which case they are technically not orphan.
   */
  @computed
  get orphanPanoramasCount() {
    if (this.floorsDisabled) {
      return 0;
    }

    if (!this.bucketFloor) {
      return 0;
    }

    return this.bucketFloor.panoramas.length;
  }

  @computed
  get newPanoramasCount() {
    // count all panos including those in the bucket and in the floors.
    let newPanoramasCount = 0;
    const allFloors = this.getAllFloors();

    for (let i = 0; i < allFloors.length; i += 1) {
      newPanoramasCount += allFloors[i].panoramas.length;
    }

    return newPanoramasCount;
  }

  @computed
  get hasNewPanoramas() {
    return this.newPanoramasCount > 0;
  }

  @computed
  get selectedPanoramasCount() {
    let count = 0;
    const allFloors = this.getAllFloors();

    for (let i = 0; i < allFloors.length; i += 1) {
      /** @type {Immersive3dTourFloor} */
      const floor = allFloors[i];
      count += floor.panoramas.filter(p => p.isSelected).length;
    }

    return count;
  }

  /**
   * Tell if the user had already processed panoramas, and that now they are trying to process more.
   * This method/value should be used in the context after the user has uploaded panoramas, at the PanoramasSection.
   */
  @computed
  get floorsDisabled() {
    const { immersive3dTour } = this.immersive3dTourStore;

    // disable floors while the entity hasn't been fetched
    if (!immersive3dTour) {
      return true;
    }

    // Disable floors if the user had previously processed panoramas.
    if (immersive3dTour.panoramasCount > 0) {
      return true;
    }

    // disable floors if the immersive 3d tour is of type Commercial
    if (immersive3dTour.isCommercial) {
      return true;
    }

    return false;
  }

  @computed
  get isReprocessingPanoramas() {
    const { immersive3dTour } = this.immersive3dTourStore;

    // disable floors while the entity hasn't been fetched
    if (!immersive3dTour) {
      return false;
    }

    return immersive3dTour.panoramasCount > 0;
  }

  @computed
  get unusedFloorsCount() {
    let count = 0;

    for (let i = 0; i < this.floors.length; i += 1) {
      const floor = this.floors[i];

      if (floor.panoramas.length === 0) {
        count += 1;
      }
    }

    return count;
  }

  @action
  initializeBucketFloor() {
    let bucketName;

    if (this.isReprocessingPanoramas) {
      const date = new Date();
      bucketName = `New Panoramas [${date.toISOString()}]`;
    } else if (this.floorsDisabled) {
      bucketName = "New Panoramas";
    } else {
      bucketName = `Drag panoramas from this bucket into the floors below. All panoramas must be moved from this
      section to process your 3D Tour.`;
    }

    if (!this.bucketFloor) {
      this.bucketFloor = new Immersive3dTourFloor({});
    }

    this.bucketFloor.setName(bucketName);
  }

  @action
  initializePropertyFloors() {
    for (let i = 0; i < this.propertyFloorsCount; i += 1) {
      this.addSequentialFloor();
    }
  }

  /**
   * Use this method when adding multiple floors is enabled and when selected version is Residential
   */
  @action
  addSequentialFloor() {
    if (this.floorsDisabled) {
      throw new Error("Cannot add sequential floor when floors are disabled");
    }

    const floorNumber = this.getNextFloorNumber();

    this.floors.push(new Immersive3dTourFloor({ name: `Floor #${floorNumber}` }));
    // once we add a new floor, let's also increase the counter.
    this.totalFloorsCreated += 1;
  }

  /**
   * This will be called for the first time after executing getOne() (immersive3dTour).
   * Eventually this will be called after on upload completion, by then there should already be a selected # of floors
   * and panoramas ready on the bucket. Note this is why we don't reset the bucket here, since it could blow up
   * recently uploaded panos.
   */
  @action
  initializeFloorsIfNeeded() {
    // always trigger initializeBucketFloor since it will update its name depending on the immersive3dTour state.
    this.initializeBucketFloor();

    // If there are already floors then it means the end user created them, we wouldn't want to override them.
    if (this.floors.length > 0) {
      return;
    }

    if (!this.floorsDisabled) {
      this.initializePropertyFloors();
    }
  }

  getNextFloorNumber() {
    // try to find the last used floor from the floors names if possible. This will make the floor numbering more
    // accurate.
    let floorsNumbers = [];

    for (let i = 0; i < this.floors.length; i += 1) {
      const { name } = this.floors[i];

      /** @type {Array} */
      const matches = name.match(/\d+/g);

      if (matches) {
        floorsNumbers = floorsNumbers.concat(matches);
      }
    }

    const highestNumber = floorsNumbers.reduce((previous, current) => {
      return Math.max(previous, current);
    }, 0);

    // Either return the highest current floor plus  one; or fallback to the current number of floors plus one.
    return highestNumber > 0 ? highestNumber + 1 : this.totalFloorsCreated + 1;
  }

  @action
  removeFloor(floorId, movePanoramasToFloorId = null) {
    if (this.floors.length < 2) {
      throw new Error("Cannot remove floor unless there are two or more floors available");
    }

    const floor = this.floors.find(f => f.id === floorId);
    const indexAt = this.floors.indexOf(floor);

    // Immediately remove the floor from the list.
    this.floors.splice(indexAt, 1);

    let newParentFloor;

    if (movePanoramasToFloorId) {
      newParentFloor = this.floors.find(f => f.id === movePanoramasToFloorId);
    } else {
      // default the newParentFloor to the first floor in the array
      [newParentFloor] = this.floors;
    }

    newParentFloor.panoramas = newParentFloor.panoramas.concat(floor.panoramas);

    this.refreshPanoramas();
  }

  @action
  resetFloors() {
    this.thumbnails = {};
    this.floors.clear();
    this.totalFloorsCreated = 0;
    this.initializeFloorsIfNeeded();
    this.bucketFloor.panoramas.clear();
  }

  @action
  reorderFloorsByIds(sourceId, targetId) {
    const floors = [...this.floors];
    const sourceFloor = floors.find(floor => floor.id === sourceId);
    const targetFloor = floors.find(floor => floor.id === targetId);

    // find indexes
    const sourceIndex = floors.indexOf(sourceFloor);
    const targetIndex = floors.indexOf(targetFloor);

    const [removed] = floors.splice(sourceIndex, 1);
    floors.splice(targetIndex, 0, removed);

    this.floors = floors;
  }

  @action
  updateFloorNameById(floorId, value) {
    const floor = this.floors.find(floorInLoop => floorInLoop.id === floorId);

    if (!floor) {
      throw new Error(`Could not find floor by id: ${floorId}`);
    }

    floor.setName(value);

    this.checkFloorNameErrors(floor);
  }

  @action
  checkFloorNameErrors(floor) {
    // remove errors.
    floor.setError(null);

    // if the floor has no name, then error.
    if (floor.name.trim().length === 0) {
      floor.setError("name is required");

      return;
    }

    // Detect duplicate names.
    for (let i = 0; i < this.floors.length; i += 1) {
      const floorInLoop = this.floors[i];

      if (floor.id !== floorInLoop.id && floor.name.toLowerCase() === floorInLoop.name.toLowerCase()) {
        floor.setError("name must be unique");

        return;
      }
    }
  }

  @action
  createUploader() {
    this.fileMetadataCache = {};

    this.uploader = Uppy({
      // Allows retries
      allowMultipleUploads: true,
      restrictions: { allowedFileTypes: [".jpg", ".jpeg"] },
    });

    const { immersive3dTour } = this.immersive3dTourStore;
    const { tourId } = immersive3dTour;
    const implementation = "immersive3dTourPanoramas";

    const getAwsS3UploadSignedUrl = () => {
      return this.awsS3UploadSignedUrlStore.getOne({
        implementation,
        key: `immersive3dTourPanoramas/${tourId}/${uuidv4()}.jpg`,
        contentType: "image/jpeg",
      });
    };

    this.uploader.use(AwsS3, {
      limit: 3,
      getUploadParameters() {
        return getAwsS3UploadSignedUrl().then(data => {
          return {
            method: "PUT",
            url: data.url,
            fields: null,
            headers: {
              "Content-Type": "image/jpeg",
            },
          };
        });
      },
    });

    // Validate files client-side after they are added.
    // Extract metadata and remove non-equirectangular files before upload starts.
    this.uploader.on("file-added", file => {
      // Cache the pending metadata so an upload finishing before extraction still gets the real values
      this.fileMetadataCache[file.id] = validateAndExtractMetadata(file.data).then(
        result => {
          if (!result.valid) {
            result.errors.forEach(error => {
              this.uploader.info(error, "error", 5000);
            });

            this.uploader.removeFile(file.id);
          }

          return result.metadata;
        },
        () => {
          this.uploader.info(
            `Image ${file.name} could not be read. Please make sure it is a valid JPG file.`,
            "error",
            5000,
          );
          this.uploader.removeFile(file.id);

          return null;
        },
      );
    });

    this.uploader.on("upload-success", (file, response) => {
      Promise.resolve(this.fileMetadataCache[file.id]).then(metadata => {
        this.onFileUploadedToS3(file, response.body, metadata);
      });
    });

    this.uploader.on("upload", () => {
      this.isUploading = true;
    });

    this.uploader.on("error", () => {
      this.isUploading = false;
    });

    // On upload error
    this.uploader.on("upload-error", () => {
      // Flag that shows there was an error on an upload
      this.hasUppyUploadError = true;
    });

    this.uploader.on("cancel-all", () => {
      this.isUploading = false;
      this.thumbnails = {};
    });

    this.uploader.on("complete", result => {
      this.isUploading = false;

      // After a retry if there is no failed upload
      // Let's remove the flag for failed uploads
      if (result.failed.length === 0) {
        this.hasUppyUploadError = false;
      }

      if (this.hasNewPanoramas) {
        this.forceShowUpload = false;
        this.refreshPanoramas();
      }
    });

    // single thumbnail generated
    this.uploader.on("thumbnail:generated", this.onThumbnailGenerated.bind(this));
  }

  @action
  closeUploader() {
    this.uploader && this.uploader.close();
  }

  @action
  onFileUploadedToS3(file, response, cachedMetadata) {
    if (!response.location) {
      return false;
    }

    // Extract the S3 key from the response URL
    const url = new URL(response.location);
    const urlPathnameParts = url.pathname.split("/");
    const s3Key = urlPathnameParts.splice(2).join("/");

    // Metadata from client-side extraction
    const metadata = cachedMetadata || {
      exifReadable: false,
      equirectangular: false,
      dateTimeOriginal: false,
      width: 0,
      height: 0,
    };

    const panorama = new Immersive3dTourPanorama({
      id: s3Key,
      upload: file,
      url: response.location,
      size: file.size,
      metadata,
    });

    this.bucketFloor.panoramas.push(panorama);
    this.refreshPanoramas();

    return true;
  }

  @action
  async onThumbnailGenerated(file, preview) {
    const name = file.name.replace(/\.[^/.]+$/, "");
    const extension = file.name.split(".").pop();
    const blobFile = await fetch(preview).then(r => r.blob());
    let thumbFile;

    // Edge Hack
    // ---------
    // Edge doesn't support File constructor so instead we're using Blob and filling missing fields so it will look like a File
    if (typeof window.navigator.msSaveBlob !== "undefined") {
      thumbFile = new Blob([blobFile], { type: extension });

      thumbFile.name = `${name}_thumb.${extension}`;
      thumbFile.lastModified = new Date();
    } else {
      thumbFile = new File([blobFile], `${name}_thumb.${extension}`, { lastModified: new Date() });
    }

    this.thumbnails[file.id] = thumbFile;
  }

  @action
  deletePanoramaWithId(panoramaId) {
    const allFloors = this.getAllFloors();

    allFloors.forEach(floor => {
      if (floor.hasPanoramaId(panoramaId)) {
        floor.removePanoramaWithId(panoramaId);
      }
    });
  }

  @action
  refreshPanoramas() {
    const allFloors = this.getAllFloors();

    for (let i = 0; i < allFloors.length; i += 1) {
      this.sortPanoramas(allFloors[i]);
    }
  }

  @action
  sortPanoramas(floor) {
    const sortByFilename = (panorama1, panorama2) => {
      const panorama1Number = panorama1.upload.name.match(/(\d+)/g);
      const panorama2Number = panorama2.upload.name.match(/(\d+)/g);

      if (panorama1Number && panorama2Number) {
        return Number(panorama1Number[0]) - Number(panorama2Number[0]);
      }

      return 0;
    };

    const sortByDateTime = (panorama1, panorama2) => {
      const { dateTimeOriginal: dateTimeOriginalFirst } = panorama1.metadata;
      const { dateTimeOriginal: dateTimeOriginalSecond } = panorama2.metadata;

      if (dateTimeOriginalFirst && dateTimeOriginalSecond) {
        return dateTimeOriginalFirst - dateTimeOriginalSecond;
      }

      return 0;
    };

    if (this.imageFilenamesFollowPattern(floor)) {
      floor.setPanoramas([...floor.panoramas].sort(sortByFilename));
    } else if (this.allImagesHaveDateTime(floor)) {
      floor.setPanoramas([...floor.panoramas].sort(sortByDateTime));
    } else {
      floor.setPanoramas([...floor.panoramas].sort(sortByFilename));
    }
  }

  /**
   * @param {array} panoramasIds
   * @param {string} floorId
   */
  @action
  movePanoramasToFloor(panoramasIds, floorId) {
    /** @type {Immersive3dTourFloor} */
    const targetFloor = this.floors.find(floor => floor.id === floorId);

    if (!targetFloor) {
      throw new Error(`Could not find target floor with id ${floorId}`);
    }

    // Filter out those panos are already belong to the target floor.
    /** @type {array} */
    const targetFloorPanoramasIds = targetFloor.panoramas.map(p => p.id);
    const filteredPanoramasIds = panoramasIds.filter(id => targetFloorPanoramasIds.indexOf(id) === -1);

    const allFloors = this.getAllFloors();

    for (let i = 0; i < allFloors.length; i += 1) {
      /** @type {Immersive3dTourFloor} */
      const sourceFloor = allFloors[i];

      for (let j = 0; j < filteredPanoramasIds.length; j += 1) {
        const panoramaId = filteredPanoramasIds[j];
        const panorama = sourceFloor.panoramas.find(p => p.id === panoramaId);

        if (panorama) {
          // remove panorama from source floor
          const panoAtIndex = sourceFloor.panoramas.indexOf(panorama);
          sourceFloor.panoramas.splice(panoAtIndex, 1);
          // add panorama to target floor
          targetFloor.panoramas.push(panorama);
        }
      }
    }

    this.refreshPanoramas();
  }

  /**
   * Mark given pano as selected while unselecting all other panos.
   */
  @action
  setPanoramaSelectedGreedy(panoramaId) {
    this.walkPanoramas(panorama => {
      panorama.setIsSelected(panorama.id === panoramaId);
    });

    this.lastSelectedPanoramaId = panoramaId;
  }

  /**
   * Mark given pano as selected.
   */
  @action
  setPanoramaSelected(panoramaId) {
    this.walkPanoramas(panorama => {
      if (panorama.id === panoramaId) {
        panorama.setIsSelected(true);
        // break
        return true;
      }

      return false;
    });

    this.lastSelectedPanoramaId = panoramaId;
  }

  @action
  togglePanoramaSelected(panoramaId) {
    this.walkPanoramas(panorama => {
      if (panorama.id === panoramaId) {
        panorama.setIsSelected(!panorama.isSelected);
        this.lastSelectedPanoramaId = panorama.isSelected ? panoramaId : null;

        // break
        return true;
      }

      return false;
    });
  }

  /**
   * Handle selecting a panorama when the user is holding the SHIFT key (span multiple panoramas)
   */
  @action
  spanPanoramaSelection(panoramaId) {
    // If there are no panorama selected already then count this action as a single selection.
    if (!this.lastSelectedPanoramaId) {
      this.setPanoramaSelected(panoramaId);
      return;
    }

    // If there's already a panorama selected but it's the same as the one we are currently trying to
    // select, then count it as a single panorama selection.
    if (this.lastSelectedPanoramaId === panoramaId) {
      this.setPanoramaSelected(panoramaId);
      return;
    }

    // find the floors where the latest selected panorama belongs to and the floor where the current selected
    // panorama belongs to.
    const allFloors = this.getAllFloors();
    const lastFloor = allFloors.find(floor =>
      floor.panoramas.find(panorama => panorama.id === this.lastSelectedPanoramaId),
    );

    const currentFloor = allFloors.find(floor => floor.panoramas.find(panorama => panorama.id === panoramaId));

    // In order to do a multi span selection, the last selected panorama and the current selected panorama
    // must belong to the same floor. If not, then treat it as a simple pano selection.
    if (lastFloor !== currentFloor) {
      this.setPanoramaSelected(panoramaId);
      return;
    }

    // Selection is happening on the same floor.
    // We'll mark selected all panos in between the two panos.
    const lastPanorama = currentFloor.panoramas.find(p => p.id === this.lastSelectedPanoramaId);
    const currentPanorama = currentFloor.panoramas.find(p => p.id === panoramaId);
    const lastIndex = currentFloor.panoramas.indexOf(lastPanorama);
    const currentIndex = currentFloor.panoramas.indexOf(currentPanorama);

    let indexFrom;
    let indexTo;

    // span from left to right?
    if (lastIndex < currentIndex) {
      // add one so we don't iterate over it in the next loop.
      indexFrom = lastIndex + 1;
      indexTo = currentIndex;
    } else {
      // span from right to left;
      indexFrom = currentIndex;
      // remove one so we don't iterate over it in the next loop.
      indexTo = lastIndex - 1;
    }

    // will be used to know if we should perform a selection or deselection.
    let selectedValue = false;

    for (let i = indexFrom; i <= indexTo; i += 1) {
      const panorama = currentFloor.panoramas[i];

      if (!panorama.isSelected) {
        selectedValue = true;
        break;
      }
    }

    for (let i = indexFrom; i <= indexTo; i += 1) {
      const panorama = currentFloor.panoramas[i];
      panorama.setIsSelected(selectedValue);
    }

    // Note that we don't set the panoramaId as the lastSelected pano id. This is on purpose since
    // it offers the best UX.
  }

  @action
  unselectAllPanoramas() {
    this.walkPanoramas(panorama => panorama.setIsSelected(false));
    this.lastSelectedPanoramaId = null;
  }

  getAllFloors() {
    const allFloors = [...this.floors];

    if (this.bucketFloor) {
      allFloors.push(this.bucketFloor);
    }

    return allFloors;
  }

  @action
  walkPanoramas(callback) {
    const allFloors = this.getAllFloors();

    for (let i = 0; i < allFloors.length; i += 1) {
      const floor = allFloors[i];

      for (let j = 0; j < floor.panoramas.length; j += 1) {
        const panorama = floor.panoramas[j];

        // If the callback returns true, then we'll interpret it as a break.
        if (callback(panorama)) {
          return;
        }
      }
    }
  }

  @action
  setDraggingPanoramaId(panoramaId) {
    this.draggingPanoramaId = panoramaId;
  }

  @action
  setPropertyFloorsCount(count) {
    this.propertyFloorsCount = count;
  }

  getFileNameWithoutExtension(fileName) {
    return fileName
      .split(".")
      .slice(0, -1)
      .join(".");
  }

  isStringNumeric(string) {
    return /^\d+$/.test(string);
  }

  imageFilenamesFollowPattern(floor) {
    // No panoramas at the floor
    if (floor.panoramas.length === 0) {
      return false;
    }

    // Let's try to find the following 2 patterns
    // a) All numbers
    // b) Same string with numbers at the end
    let patternType = "";
    let sameString = "";
    let sameStringLastCharacterIndex = null;

    // Get the file name without the extension of the first panorama of the floor
    const fileNameWithoutExtension = this.getFileNameWithoutExtension(floor.panoramas[0].upload.name);

    // Search for the pattern type
    if (this.isStringNumeric(fileNameWithoutExtension)) {
      patternType = "allNumbers";
    } else {
      // The next check if there is a pattern of mixed string and numbers
      patternType = "sameStringWithNumbers";

      // We will go from the end to the beginning
      for (let i = fileNameWithoutExtension.length - 1; i >= 0; i -= 1) {
        // At least the last digit should be numeric
        if (i === fileNameWithoutExtension.length - 1 && !this.isStringNumeric(fileNameWithoutExtension[i])) {
          return false;
        }

        // We don't know how many other characters are numeric
        // But if we face a non numeric character
        // That means that this character will be the last character of the pattern
        if (!this.isStringNumeric(fileNameWithoutExtension[i])) {
          sameStringLastCharacterIndex = i;
          break;
        }
      }

      // Strange, but we could not find any pattern
      if (sameStringLastCharacterIndex === null) {
        return false;
      }

      // Finally, what we were waiting for
      // We got the string part before the number
      sameString = fileNameWithoutExtension.substr(0, sameStringLastCharacterIndex + 1);
    }

    // Now, let's check if all images follow the same pattern
    for (let i = 0; i < floor.panoramas.length; i += 1) {
      const fileName = this.getFileNameWithoutExtension(floor.panoramas[i].upload.name);

      if (
        (patternType === "allNumbers" && !this.isStringNumeric(fileName)) ||
        (patternType === "sameStringWithNumbers" && !fileName.startsWith(sameString))
      ) {
        return false;
      }
    }

    return true;
  }

  allImagesHaveDateTime(floor) {
    // No panoramas at the floor
    if (floor.panoramas.length === 0) {
      return false;
    }

    return floor.panoramas.every(panorama => panorama.metadata.dateTimeOriginal);
  }

  @computed
  get imageUrls() {
    const imageUrls = [];
    const allFloors = this.getAllFloors();

    allFloors.forEach(floor => {
      floor.panoramas.forEach(panorama => {
        imageUrls.push(panorama.url);
      });
    });

    return imageUrls;
  }

  @action
  process(tourJobTypeId, notes, estimatedShootSize = null) {
    this.refreshPanoramas();

    if (!this.bucketFloor) {
      return Promise.reject(new Error("Expected bucket floor to  exist at this point"));
    }

    let floor;
    let panorama;

    // note that on the residential version, the bucket should be empty and the floors filled with panos; but when
    // user tries to upload a new batch of panos we'll disable floors and all panos will be on the bucket floor.
    // On the commercial, only the bucket should contain panos. Here we combine them all since the mentioned
    // restrictions belong to the UI and shouldn't affect the outcome here.

    const allFloors = [this.bucketFloor, ...this.floors];
    const newPanoramasData = [];

    for (let i = 0; i < allFloors.length; i += 1) {
      floor = allFloors[i];
      const newPanoramas = [];

      for (let j = 0; j < floor.panoramas.length; j += 1) {
        panorama = floor.panoramas[j];
        if (panorama.hasErrors) {
          return Promise.reject(
            new Error(
              "One or multiple errors prevent this Immersive 3D Tour from being successfully processed. Please go back to fix these errors.",
            ),
          );
        }

        newPanoramas.push(panorama.id);
      }

      if (newPanoramas.length > 0) {
        newPanoramasData.push({
          name: floor.name,
          panoramas: newPanoramas,
        });
      }
    }

    const { immersive3dTour } = this.immersive3dTourStore;
    const { tourId } = immersive3dTour;

    const data = {
      tourJobTypeId,
      newPanoramasData,
      notes,
    };

    if (estimatedShootSize) {
      data.estimatedShootSize = estimatedShootSize;
    }

    this.errorMessages = {};

    // We replaced the load function of Immersive3dTourAppPageStore
    // with getting the new Immersive3dTour from the process function
    // and then hitting the processingAutoRefresh function
    // We do not need to get the Tour Job right away, we predict that nothing has changed
    return this.immersive3dTourStore
      .process(tourId, data)
      .then(() => {
        // We have successfully sent the data to get processed
        this.resetFloors();

        this.rootStore.processingAutoRefresh();
      })
      .catch(response => {
        if (response.status === "fail") {
          this.errorMessages = response.message;

          const errorWithValidationErrors = new Error();
          errorWithValidationErrors.hasValidationErrors = true;

          throw errorWithValidationErrors;
        } else {
          throw Error(response.message);
        }
      });
  }

  @action
  addToTour() {
    const { immersive3dTour } = this.immersive3dTourStore;

    this.isTogglingToTour = true;

    return this.immersive3dTourStore
      .update(immersive3dTour.tourId, { isReady: true })
      .catch(() => {
        this.message = {
          header: "An error occurred while adding your Immersive 3D Tour to your Tour",
          type: "negative",
        };
      })
      .finally(() => {
        this.isTogglingToTour = false;
      });
  }

  @action
  removeFromTour() {
    const { immersive3dTour } = this.immersive3dTourStore;

    this.isTogglingToTour = true;

    return this.immersive3dTourStore
      .update(immersive3dTour.tourId, { isReady: false })
      .catch(() => {
        this.message = {
          header: "An error occurred while removing your Immersive 3D Tour from your Tour",
          type: "negative",
        };
      })
      .finally(() => {
        this.isTogglingToTour = false;
      });
  }

  @action
  delete(tourId) {
    this.isLoading = true;

    return this.immersive3dTourStore.delete(tourId).then(() => {
      // Load the new Immersive 3D Tour
      return this.rootStore.load().then(() => {
        // Show upload
        this.forceShowUpload = true;
      });
    });
  }

  @action
  updateImageData(imageUrl, imageData) {
    let selectedPanorama;

    const allFloors = this.getAllFloors();

    // Find the image we are trying to update by the url
    for (let i = 0; i < allFloors.length; i += 1) {
      selectedPanorama = allFloors[i].panoramas.find(image => image.url === imageUrl);

      if (selectedPanorama) {
        break;
      }
    }

    const { immersive3dTour } = this.immersive3dTourStore;

    return this.immersive3dTourStore
      .updateImageData(immersive3dTour.tourId, {
        imageId: selectedPanorama.id,
        imageData,
      })
      .then(response => {
        // Replace the old url with new
        selectedPanorama.url = response.data.url;
      })
      .catch(response => {
        if (response.status === "fail") {
          throw new Error("Something went wrong!");
        } else {
          throw new Error(response.message);
        }
      });
  }
}

export default Immersive3dTourTabStore;
